<script>
import constants from "@/libs/Constants";
import ApiConnection from "@/libs/ApiConnection";
import Boxplot from "@/components/Charts/Boxplot.vue";
import ModalWindow from "@/components/ModalWindow.vue";
import GroupErrorTranslation from "@/libs/locales/errors";
import {VisualizationProjectionData} from "@/libs/VisualizationData";
import {
  disposeTooltipsByClass,
  downloadFileFromAElement,
  getPercentileI18NLabel,
  getVisualizationComponentI18NLabel,
  setupTooltipsByClass,
  UUIDfromList,
  yearMonthStrToMonthI18N
} from "@/libs/Helpers";
import OffCanvas from "@/components/OffCanvas.vue";

export default {
  name: 'PlotProjection',
  components: {
    OffCanvas,
    Boxplot,
    ModalWindow
  },
  emits: ['onCloseModalClick', 'onOpenModal', 'afterPlotDataLoading', 'onTimeSeriesDataError'],
  props: {
    propFormData: VisualizationProjectionData || null,
    propPlotLayersCount: {type: Number, default: null},
    propPlotLayers: Array || null,
    propPlotLayerNames: {type: Array, default: []},
    propPlotLayersTrigger: Boolean,
    propModalStatus: Boolean,
  },
  data() {
    let availableYears = [2050, 2075, 2095];
    let plotDownload = {annual: false};
    availableYears.forEach(year => {
      plotDownload[`year_${year}`] = false;
    })

    return {
      constants: constants,
      plotData: null, //Data from API
      loading: {
        data: false, //Flag for data loading from API
        file: false
      },
      offCanvasAreas: {
        show: false
      },
      lastUUID: null,
      modalStatus: false, //Status for modal to show
      plotDownload: plotDownload,
      selectedComponent: constants.PROJECTION.OVERVIEW,

      selectedYear: null,

      //Dataset processed for yearly data
      availableYears: availableYears,
      plotYearlyData: {},

      //List of datasets processed for each year for monthly data
      plotMonthlyData: {},
      plotYMin: null,
      plotYMax: null,
    }
  },
  watch: {
    propModalStatus(newValue, oldValue) {
      if (this.modalStatus !== newValue)
        this.modalStatus = newValue;
    },
    propPlotLayersTrigger(newValue, oldValue) {
      if (this.propFormData !== null && this.propPlotLayers !== null && this.propPlotLayers.length > 0) {
        /***
         * To avoid a request of data using the same parameters, the idea
         * is to build a UUID of the parameters (including the selected features)
         * and check whether the data is already present in the time-series or not.
         *
         * If the last UUID corresponds to the new one, then this method will only
         * send the signal to open the modal with data already loaded, otherwise
         * it makes the API request and sends the retrieved data.
         ***/
        let uuidList = [
          this.propFormData.component,
          this.propFormData.variable,
          this.propFormData.percentile,
          this.propFormData.year,
          this.propPlotLayers.sort().join(','),
          this.$i18n.locale // Forces data to reload when language changes outside the component
        ];

        let newUUID = UUIDfromList(uuidList);
        if (this.plotData === null || this.lastUUID === null || this.lastUUID !== newUUID) {
          this.loading.data = true;

          ApiConnection.postProjectionTimeseries(
              this.propFormData.variable,
              this.propPlotLayers
          ).then(response => {
            response.json().then(output => {
              //Update plot data
              this.initializePlotDatasets();

              this.plotData = output;
              //Monthly data by year
              let outputMonthly = output.monthly;

              let monthlyData = outputMonthly.values;
              monthlyData.forEach(monthItem => {
                this.plotMonthlyData[monthItem.year.toString()] = this.plotDatasetsProcess(
                    monthItem.scenarios,
                    true
                );
              });

              if (outputMonthly.minimum !== undefined && outputMonthly.minimum !== null)
                this.plotYMin = Math.ceil(outputMonthly.minimum - 5.0);

              if (outputMonthly.maximum !== undefined && outputMonthly.maximum !== null)
                this.plotYMax = Math.ceil(outputMonthly.maximum + 5.0);

              //Yearly data
              this.plotYearlyData = this.plotDatasetsProcess(this.plotData.yearly);

              //Update last UUID
              this.lastUUID = newUUID;

              //Open modal
              this.modalOpen();

              this.selectedComponent = constants.PROJECTION.OVERVIEW;
              this.selectedYear = this.propFormData.year.toString();
            });
          }).catch((error) => {
            //Send error to parent
            this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
          }).finally(() => {
            //Unblock buttons
            this.loading.data = false;
            this.$emit('afterPlotDataLoading');
          });

        } else {
          this.$emit('afterPlotDataLoading');

          //Open modal
          this.modalOpen();
        }

        this.$nextTick(() => {
          this.tooltipsSetup();
          let object = document.getElementById('projection-plot-offcanvas-link');
          if (object !== undefined && object !== null) {
            object.removeEventListener('click', this.offCanvasAreasShowOnClick);
            object.addEventListener('click', this.offCanvasAreasShowOnClick);
          }
        });
      }
    }
  },
  computed: {
    modalTitle() {
      let title = '';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        title = variableConfiguration.getNameI18N(this.$i18n.locale)
      }
      return title
    },
    modalTitle2Objects() {
      let items = {};
      if (this.propFormData !== null) {
        if (this.propFormData.visualizationMode === constants.VISUALIZATION_MODE.ADVANCED) {
          let percentileLabel = getPercentileI18NLabel(this.propFormData.percentile);
          items[this.$t('InputSearchFormLabelTitle.Percentile')] = this.$t(percentileLabel);
        }
      }

      return items;
    },
    plotVariableNameUnit() {
      let title = this.modalTitle;

      let unit = '';
      if (this.propFormData !== null) {
        let variableConfiguration = this.propFormData.variableConfiguration;
        unit = variableConfiguration.unit;
      }

      if (unit !== '')
        title = `${title} [${unit}]`
      return title;
    },
    plotYAxis() {
      return this.plotVariableNameUnit;
    },
    plotTitle() {
      return this.plotVariableNameUnit;
    },
    plotFileName() {
      let name = 'projection';
      if (this.propFormData !== null) {
        name = `${name}_${this.propFormData.variable}`;

        switch (this.selectedComponent) {
          case constants.PROJECTION.MONTH:
            name = `${name}_monthly_${this.selectedYear}`;
            break;
          default:
            name = `${name}_annual`;
            break;
        }
      }
      return name;
    },
    plotMonthlyAnnotations() {
      let config = {
        lineWinter: {
          label: this.$t('InputSeasons.winter'),
          xAdjust: 5,
          xPosition: -0.5,
          borderWidth: 0
        },
        lineSpring: {
          label: this.$t('InputSeasons.spring'),
          xAdjust: 40,
          xPosition: 1.5
        },
        lineSummer: {
          label: this.$t('InputSeasons.summer'),
          xAdjust: 35,
          xPosition: 4.5
        },
        lineFall: {
          label: this.$t('InputSeasons.fall'),
          xAdjust: 35,
          xPosition: 7.5
        },
        lineWinterNext: {
          label: this.$t('InputSeasons.winter'),
          xAdjust: 35,
          xPosition: 10.5
        }
      };

      let configuration = {};
      Object.keys(config).forEach((key) => {
        let configData = config[key];

        configuration[key] = {
          type: 'line',
          display: true,
          label: {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            color: 'black',
            content: configData.label,
            display: true,
            position: 'end',
            textAlign: 'right',
            xAdjust: configData.xAdjust,
            yAdjust: -2
          },
          borderColor: '#505050',
          borderWidth: configData.borderWidth !== undefined ? configData.borderWidth : 1,
          xMin: configData.xPosition,
          xMax: configData.xPosition
        }
      });

      return configuration;
    }
  },
  beforeMount() {
    this.initializePlotDatasets();
  },
  methods: {
    getVisualizationComponentI18NLabel,
    descriptionLabelI18NGet() {
      let params = {variable: this.modalTitle};

      let labelPostfix = 'area_one';
      let labelTooltipContent = '&nbsp;';

      if (this.propPlotLayers !== null && this.propPlotLayersCount !== null) {
        let selectedLayerCount = this.propPlotLayers.length;

        if (selectedLayerCount > 0 && this.propPlotLayersCount > 0) {
          if (selectedLayerCount === 1) {
            labelPostfix = 'area_one';

            if (this.propPlotLayerNames !== null) {
              labelTooltipContent = this.propPlotLayerNames.toString();
              labelTooltipContent = labelTooltipContent.replaceAll('"', "&#34;");
            }
          } else if (selectedLayerCount < this.propPlotLayersCount) {
            labelPostfix = 'area_many'
            params.area_count = selectedLayerCount;
          } else
            labelPostfix = 'area_all'
        }
      }

      params.tooltip_content = labelTooltipContent;
      return this.$t(`VisualizationPlot.projection.${labelPostfix}`, params);
    },
    initializePlotDatasets() {
      this.plotYearlyData = {
        datasets: [],
        labels: []
      }
      this.plotMonthlyData = {};
      this.availableYears.forEach((year) => {
        this.plotMonthlyData[year.toString()] = {
          datasets: [],
          labels: []
        };
      });
    },
    plotDatasetsProcess(scenarioOutput, convertYearMonthToMonth = false) {
      /**
       * Initialize items for each scenario label
       * Each list of scenario contains a list of data for a specific year
       **/
      let data_items = {};
      let ssps_dataset = {};

      Object.keys(this.constants.SSPS).forEach((key) => {
        let ssps_key = this.constants.SSPS[key];

        data_items[ssps_key] = {};
        ssps_dataset[ssps_key] = [];
      });

      let datasets = [];
      let availableLabels = [];

      if (scenarioOutput !== null) {
        const mappings = {
          minimum: 'min',
          lower_quantile: 'q1',
          median: 'median',
          //mean: 'mean', //Remove comment to show mean value again
          upper_quantile: 'q3',
          maximum: 'max',
          outliers: 'outliers'
        };

        /**
         * Scenario data hierarchy for yearly data
         * [
         *    'scenario': 'scenario_name',
         *    'classes': [
         *      'label': 2050,
         *      'type': 'summary|values',
         *      'values': [list of values],
         *      'summary': {
         *        'minimum': 1,
         *        'lower_quantile': 2,
         *        'median': 3,
         *        'mean': 4,
         *        'upper_quantile': 5,
         *        'maximum': 6,
         *        'outliers': [list of values]
         *      }
         *    ]
         * ]
         */
        scenarioOutput.forEach(scenarioItem => {
          let scenarioKey = scenarioItem.scenario;
          let scenarioYears = scenarioItem.classes;

          if (scenarioYears !== undefined && scenarioYears.length > 0) {
            if (!data_items.hasOwnProperty(scenarioKey))
              data_items[scenarioKey] = {};

            scenarioYears.forEach(yearItem => {
              let scenarioYearLabel = yearItem.label;

              if (convertYearMonthToMonth)
                scenarioYearLabel = yearMonthStrToMonthI18N(scenarioYearLabel, this.$i18n, 'long');

              if (availableLabels.indexOf(scenarioYearLabel) === -1)
                availableLabels.push(scenarioYearLabel);

              if (yearItem.type === 'summary') {
                let scenarioValues = yearItem.summary;
                /**
                 * Retrieve summary with quartiles, minimum, maximum, mean and median
                 */
                let valueConfiguration = {};
                Object.keys(mappings).forEach(mappingKey => {
                  let key = mappings[mappingKey];

                  let value = scenarioValues[mappingKey];
                  if (value !== undefined)
                    valueConfiguration[key] = value;
                });

                data_items[scenarioKey][scenarioYearLabel] = valueConfiguration;


              } else if (yearItem.type === 'values') {
                /**
                 * Retrieve raw values
                 * Each pushed item corresponds to a year
                 */
                data_items[scenarioKey][scenarioYearLabel] = yearItem.values;
              }
            })
          }
        });

        //Keep datasets ordered by available year
        Object.keys(ssps_dataset).forEach(scenarioKey => {
          availableLabels.forEach(yearItem => {
            ssps_dataset[scenarioKey].push(data_items[scenarioKey][yearItem])
          });
        })

        let selectedScenario = null;
        if (this.propFormData !== null)
          selectedScenario = this.propFormData.climateScenario;

        let lineZero = [];
        for (let i = 0; i < ssps_dataset[constants.SSPS.SSPS5_8_5].length; i++)
          lineZero.push(0);

        datasets = [
          {
            label: this.$t('InputSSPLabel.SSP1_2-6'),
            borderColor: 'rgb(50,113,36)',
            borderWidth: selectedScenario === constants.SSPS.SSPS1_2_6 ? 4 : 2, //Ticker border based on selected scenario
            backgroundColor: 'rgba(50,113,36,0.25)',
            data: ssps_dataset[constants.SSPS.SSPS1_2_6]
          },
          {
            label: this.$t('InputSSPLabel.SSP2_4-5'),
            borderColor: 'rgb(213,162,12)',
            borderWidth: selectedScenario === constants.SSPS.SSPS2_4_5 ? 4 : 2,
            backgroundColor: 'rgba(244,184,15,0.25)',
            data: ssps_dataset[constants.SSPS.SSPS2_4_5]
          },
          {
            label: this.$t('InputSSPLabel.SSP5_8-5'),
            borderColor: 'rgba(220,53,69)',
            borderWidth: selectedScenario === constants.SSPS.SSPS5_8_5 ? 4 : 2,
            backgroundColor: 'rgba(220,53,69,0.25)',
            data: ssps_dataset[constants.SSPS.SSPS5_8_5]
          }
        ];

        if (this.propFormData !== null && this.propFormData.variableConfiguration !== null && lineZero.length > 0) {
          let backgroundColorAbove = this.propFormData.variableConfiguration.getBackgroundAboveRGBA(0.1);
          let backgroundColorBelow = this.propFormData.variableConfiguration.getBackgroundBelowRGBA(0.1);

          if (backgroundColorAbove !== null && backgroundColorBelow !== null) {
            datasets.push({
              type: 'line',
              data: lineZero,
              fill: {
                target: 'end',
                below: backgroundColorAbove, // Read from variable configuration
              },

              //Hidden line
              label: constants.LABELS.NO_LABEL,
              pointBorderWidth: 0,
              borderWidth: 0, // No line
              pointRadius: 0,
              pointHoverRadius: 0
            });

            datasets.push({
              type: 'line',
              data: lineZero,
              fill: {
                target: 'start',
                above: backgroundColorBelow // Read from variable configuration
              },
              showLine: false,
              hidden: false,
              //Hidden line
              label: constants.LABELS.NO_LABEL,
              pointBorderWidth: 0,
              borderWidth: 0,
              pointRadius: 0,
              pointHoverRadius: 0,
              borderColor: 'transparent'
            });
          }
        }
      }

      return {
        datasets: datasets,
        labels: availableLabels
      };
    },
    getPlotOverviewDatasets() {
      let output = [];
      if (this.plotYearlyData !== null) {
        output = this.plotYearlyData.datasets;
        if (output === undefined)
          output = []
      }

      return output;
    },
    getPlotOverviewLabels() {
      let output = [];
      if (this.plotYearlyData !== null) {
        output = this.plotYearlyData.labels;
        if (output === undefined)
          output = []
      }

      return output;
    },
    getPlotMonthlyDataDatasets(year) {
      let monthlyDataset = [];
      let output = this.plotMonthlyData[year.toString()];

      if (output !== undefined && output !== null) {
        monthlyDataset = output.datasets;
        if (monthlyDataset === undefined)
          monthlyDataset = []
      }
      return monthlyDataset;
    },
    getPlotMonthlyDataLabels(year) {
      let monthlyDataset = [];
      let output = this.plotMonthlyData[year.toString()];
      if (output !== undefined && output !== null) {

        monthlyDataset = output.labels;
        if (monthlyDataset === undefined) {
          monthlyDataset = []
        }
      }
      return monthlyDataset;
    },
    modalOpen() {
      this.modalStatus = true;
      this.$emit('onOpenModal');
    },
    modalOnClose() {
      this.modalStatus = false;
      this.$emit('onCloseModalClick');
    },
    selectComponent(component) {
      this.selectedComponent = component;
    },
    downloadImageButtonOnClick() {
      let key = 'annual'
      if (this.selectedComponent === constants.PROJECTION.MONTH)
        key = `year_${this.selectedYear}`;
      this.plotDownload[key] = !this.plotDownload[key];
    },
    downloadFileButtonOnClick() {
      if (this.propFormData !== null && this.propPlotLayers.length > 0) {
        this.loading.data = true;
        this.loading.file = true;

        let apiResponse;
        switch (this.selectedComponent) {
          case constants.PROJECTION.MONTH:
            let selectedYear = parseInt(this.selectedYear);
            if (isNaN(selectedYear) === true)
              selectedYear = this.selectedYear;

            apiResponse = ApiConnection.postProjectionTimeseriesDownloadMonthly(
                this.propFormData.variable,
                selectedYear,
                this.propPlotLayers
            );

            break;
          default:
            apiResponse = ApiConnection.postProjectionTimeseriesDownloadAnnual(
                this.propFormData.variable,
                this.propPlotLayers
            );
            break;
        }

        apiResponse.then(response => {
          let fileName = response.headers.get('filename');
          if (fileName === null)
            fileName = `${this.plotFileName}.csv`

          response.blob().then(data => {
            /*Create link with BLOB data to download*/
            downloadFileFromAElement(fileName, data);
          });
        }).catch((error) => {
          //Send error to parent
          this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
        }).finally(() => {
          this.loading.data = false;
          this.loading.file = false;
        });
      }
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-modal-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-modal-init');
    },
    offCanvasAreasShowOnClick() {
      this.offCanvasAreas.show = true;
    },
    offCanvasAreasOnHidden() {
      this.offCanvasAreas.show = false;
    }
  }
}
</script>

<template>
  <ModalWindow prop-id="modalGraphBoxplot"
               prop-size="xl"
               :prop-static="true"
               :prop-status="modalStatus"
               @on-close-modal-click="modalOnClose">
    <template v-slot:title>
        <span class="h5 text-primary pe-1 fw-semibold">
          {{ $t(getVisualizationComponentI18NLabel(constants.COMPONENTS.PROJECTION)) }}
        </span>{{ this.modalTitle }}
    </template>
    <template v-slot:subtitle>
      <template v-for="(value, name) in this.modalTitle2Objects">
        <span class="ms-1 badge rounded-pill bg-secondary me-1">{{ name }}</span><span>{{ value }}</span>
      </template>
    </template>
    <!-- Information -->
    <div class="row">
      <div class="col mb-1">
        <p class="fw-light lh-sm">
          <span v-html="descriptionLabelI18NGet()"></span>
          <span v-html="$t('VisualizationPlot.projection.download')"></span>
        </p>
      </div>
    </div>
    <!-- View selection -->
    <div class="row border-bottom pb-2 mb-3">
      <div class="col">
        <nav class="nav nav-pills flex-column flex-sm-row border-0 justify-content-center">
          <button role="link" class="flex-sm-fill flex-md-grow-0 nav-link fw-semibold"
                  :class="{
                            active: selectedComponent === constants.PROJECTION.OVERVIEW
                          }"
                  @click="selectComponent(constants.PROJECTION.OVERVIEW)">
            <i class="bi bi-calendar2-check pe-2"></i>
            {{ $t('SearchForm.annual') }}
          </button>
          <button role="link" class="flex-sm-fill flex-md-grow-0 nav-link fw-semibold"
                  :class="{
                            active: selectedComponent === constants.PROJECTION.MONTH
                          }"
                  @click="selectComponent(constants.PROJECTION.MONTH)">
            <i class="bi bi-calendar2-month pe-2"></i>
            {{ $t('SearchForm.month') }}
          </button>
        </nav>
      </div>
    </div>
    <!-- Plots -->
    <Transition>
      <div class="row" v-show="selectedComponent === constants.PROJECTION.OVERVIEW">
        <div class="col-12">
          <Boxplot prop-id="chartBoxplotGlobal"
                   :prop-title="plotTitle !== undefined && plotTitle !== null ? plotTitle : ''"
                   :prop-title-legend="$t('InputSearchFormLabelTitle.climateChangeScenario')"
                   :prop-datasets="getPlotOverviewDatasets()"
                   :prop-labels="getPlotOverviewLabels()"
                   :prop-download-toggle="plotDownload.annual"
                   :prop-download-file-name="plotFileName"
                   :prop-x-axis-label="$t('InputSearchFormLabelTitle.Year')"
                   :prop-y-axis-label="this.plotYAxis"
          />
        </div>
      </div>
    </Transition>
    <Transition>
      <div class="row" v-show="selectedComponent === constants.PROJECTION.MONTH">
        <div class="col-12 offset-0 col-md-8 offset-md-2 col-lg-4 offset-lg-4 mt-1 mb-4">
          <!-- Select Year -->
          <div class="row">
            <label for="yearSelectPlot" class="col-2 col-form-label">{{ $t('InputSearchFormLabelTitle.Year') }}</label>
            <div class="col-10">
              <div class="btn-group d-flex" role="group">
                <template v-for="year in availableYears">
                  <input type="radio"
                         name="projection_boxplot_year"
                         class="btn-check"
                         :id="`projection_boxplot_year_${year}`"
                         :value="year.toString()"
                         v-model="this.selectedYear"
                         autocomplete="off">
                  <label class="btn btn-outline-primary btn-l mb-0" :for="`projection_boxplot_year_${year}`">{{
                      year
                    }}</label>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <template v-for="year in availableYears">
            <Boxplot :prop-id="`chartBoxplotMonth_${year}`"
                     :prop-title="plotTitle !== undefined && plotTitle !== null ? plotTitle : ''"
                     :prop-y-min="plotYMin"
                     :prop-y-max="plotYMax"
                     :prop-download-file-name="plotFileName"
                     :prop-download-toggle="plotDownload[`year_${year}`]"
                     :prop-title-legend="$t('InputSearchFormLabelTitle.climateChangeScenario')"
                     :prop-datasets="getPlotMonthlyDataDatasets(year)"
                     :prop-labels="getPlotMonthlyDataLabels(year)"
                     :prop-y-axis-label="this.plotYAxis"
                     :prop-x-axis-label="`${$t('InputSearchFormLabelTitle.Month')} (${year})`"
                     :prop-annotations="plotMonthlyAnnotations"
                     :prop-locale="$i18n.locale"
                     v-show="selectedYear === year.toString()"
            />
          </template>
        </div>
      </div>
    </Transition>
    <template v-slot:footer>
      <div class="btn-group dropup">
        <button type="button" class="btn btn-success dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"
                :disabled="loading.data === true">
          <div v-if="loading.file === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i class="bi bi-download pe-2" v-else></i>
          <span class="pe-1">{{ $t('buttons.downloadData') }}</span>
        </button>
        <ul class="dropdown-menu">
          <li>
            <button class="dropdown-item" role="link" @click="downloadImageButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-image pe-2"></i>{{ $t('buttons.downloadDataImage') }}
            </button>
          </li>
          <li>
            <hr class="dropdown-divider">
          </li>
          <li>
            <button class="dropdown-item" role="link" @click="downloadFileButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-file-earmark-spreadsheet pe-2"></i>{{ $t('buttons.downloadDataCSV') }}
            </button>
          </li>
        </ul>
      </div>
    </template>
    <OffCanvas
        prop-id="plotProjectionOffCanvas"
        :prop-title="$tc('InputSearchFormLabelValue.Areas', propPlotLayerNames.length)"
        :prop-show="offCanvasAreas.show"
        prop-position="end"
        :prop-hide-close-button="false"
        @on-canvas-hidden="offCanvasAreasOnHidden">
      <table class="table table-sm table-hover table-bordered table-striped">
        <tbody>
        <tr v-for="(area, index) in propPlotLayerNames">
          <td class="fw-semibold text-end">{{ index + 1 }}</td>
          <td>{{ area }}</td>
        </tr>
        </tbody>
      </table>
    </OffCanvas>
  </ModalWindow>
</template>